import get from 'lodash/get';
import type { PreferredLogin } from './users/invite/inviteUserTypes';
import type { Tag } from './appTypes';
import type { RootState } from '../../configuration/setup/store';
import { useAppSelector } from '../../configuration/setup/hooks';
import { groupSidebarHasChanges } from './groups/groupsidebar/groupPropertiesService';
import { userSidebarHasChanges } from './users/usersidebar/userSidebarService';

const EMPTY_TAGS: Tag[] = [];

export const getSortByField = (sort: string | undefined) => (sort ? sort.substring(1) : '');
export const getSortOrderIsAsc = (sort: string) => (sort ? sort.charAt(0) === '+' : true);

export const getApp = (state: RootState) => get(state, 'app');

export const getGroups = (state: RootState) => state.app.groups;
export const getGroupsLoadMoreLink = (state: RootState) => state.app.groupsLoadMoreLink;
export const getRoles = (state: RootState) => state.app.roles;

export const getSelectedGroup = (state: RootState) => state.app?.selectedGroup ?? null;
export const getSelectedGroupId = (state: RootState) => get(getSelectedGroup(state), 'id', null);
export const getRolesOfSelectedGroup = (state: RootState) => getSelectedGroup(state)?.assigned_roles ?? [];
export const getPersistedNameOfSelectedGroup = (state: RootState) => get(getSelectedGroup(state), 'name', '');
export const getPersistedDescriptionOfSelectedGroup = (state: RootState) =>
    get(getSelectedGroup(state), 'description', '');

export const getSelectedUser = (state: RootState) => get(getApp(state), 'selectedUser', null);
export const getLastCreatedUserId = (state: RootState) => get(getApp(state), 'lastCreatedUserId', undefined);
export const getSelectedUserId = (state: RootState) => get(getSelectedUser(state), 'id', null);
export const getUserMfaSettingsByUserId = (state: RootState) => state.app.userMfaSettingsByUserId;
export const getUserMfaSettingsFetchError = (state: RootState) => state.app.userMfaSettingsFetchError;
export const getGroupIdsOfSelectedUser = (state: RootState): string[] | undefined =>
    get(getSelectedUser(state), 'group_ids', []);
export const getFirstNameOfSelectedUser = (state: RootState) => state.app.selectedUser?.first_name ?? '';
export const getLastNameOfSelectedUser = (state: RootState) => state.app.selectedUser?.last_name ?? '';
export const getEmailOfSelectedUser = (state: RootState) => state.app.selectedUser?.email ?? '';
export const getPhoneNumberOfSelectedUser = (state: RootState) => get(getSelectedUser(state), 'phone_number', '');
export const getLoginOfSelectedUser = (state: RootState) => get(getSelectedUser(state), 'login', '');
export const getPreferredLoginOfSelectedUser = (state: RootState): PreferredLogin =>
    get(getSelectedUser(state), 'preferred_login', 'email');

export const getSessionWarningAcknowledged = (state: RootState) =>
    get(getApp(state), 'sessionWarningAcknowledged', false);
export const getUsers = (state: RootState) => state.app.users;
export const getHasFetchedGroupsTabData = (state: RootState) => state.app.hasFetchedGroupsTabData;
export const getHasFetchedUsers = (state: RootState) => state.app.hasFetchedUsers;
export const getHasFetchedUsersTabData = (state: RootState) => state.app.hasFetchedUsersTabData;
export const getUsersLoadMoreLink = (state: RootState) => get(getApp(state), 'usersLoadMoreLink', null);
export const getTags = (state: RootState): Tag[] => get(getApp(state), 'tags', EMPTY_TAGS);
export const getAllowedGroupActions = (state: RootState) => state.app.allowedGroupActions;
export const getAllowedUserActions = (state: RootState) => state.app.allowedUserActions;
export const getAccessIsForbidden = (state: RootState) => get(getApp(state), 'accessIsForbidden', false);
export const getServerErrorCode = (state: RootState) => get(getApp(state), 'serverErrorCode', null);

export const getLoadingError = (state: RootState) => get(getApp(state), 'loadingError', null);
export const getIsLoading = (state: RootState) => get(getApp(state), 'isLoading', false);

export const getNextGroup = (state: RootState) => state.app.nextGroup;
export const getNextUser = (state: RootState) => state.app.nextUser;

export const useSelectedUser = () => useAppSelector(getSelectedUser);
export const useSelectedGroup = () => useAppSelector(getSelectedGroup);
export const useGroupSidebarHasChanges = () => useAppSelector(groupSidebarHasChanges);
export const useUserSidebarHasChanges = () => useAppSelector(userSidebarHasChanges);
